import axios from 'axios';
import { api } from '@/lib/axios';

export type PdfMode = 'open' | 'download';

/** Extrait le nom de fichier de l'en-tête Content-Disposition (RFC 5987 prioritaire). */
function filenameFromDisposition(cd: string | undefined): string | null {
  if (!cd) return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (star?.[1]) {
    try { return decodeURIComponent(star[1]); } catch { /* repli ci-dessous */ }
  }
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain?.[1] ?? null;
}

/** Récupère le PDF authentifié et renvoie une URL blob + le nom proposé par le serveur. */
async function fetchPdf(url: string, params?: Record<string, unknown>, filename?: string): Promise<{ blobUrl: string; name: string }> {
  let response;
  try {
    response = await api.get(url, { params, responseType: 'blob' });
  } catch (e) {
    // Une erreur renvoyée en JSON sur une réponse binaire arrive sous forme de Blob, illisible telle
    // quelle : on la décode pour afficher le message du serveur (« Aucune machine à imprimer… »).
    if (axios.isAxiosError(e) && e.response?.data instanceof Blob && e.response.data.type.includes('json')) {
      try { e.response.data = JSON.parse(await e.response.data.text()); } catch { /* corps illisible : on garde l'erreur d'origine */ }
    }
    throw e;
  }
  const blob = new Blob([response.data as BlobPart], { type: 'application/pdf' });
  const name = filename
    ?? filenameFromDisposition(response.headers?.['content-disposition'] as string | undefined)
    ?? 'document.pdf';
  return { blobUrl: URL.createObjectURL(blob), name };
}

/**
 * OUVRE le PDF dans un nouvel onglet (consultation / impression). L'onglet est
 * ouvert AVANT l'`await` pour conserver l'« activation utilisateur ». Repli en
 * téléchargement nommé si la fenêtre est bloquée.
 */
export async function openPdf(url: string, params?: Record<string, unknown>, filename?: string): Promise<void> {
  const win = window.open('', '_blank');
  try {
    const { blobUrl, name } = await fetchPdf(url, params, filename);
    if (win && !win.closed) {
      win.location.href = blobUrl;
      try { win.document.title = name; } catch { /* document blob inaccessible : ignoré */ }
    } else {
      triggerDownload(blobUrl, name);
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch (e) {
    if (win && !win.closed) win.close();
    throw e;
  }
}

/** TÉLÉCHARGE le PDF en l'enregistrant directement sous son nom (Content-Disposition). */
export async function downloadPdf(url: string, params?: Record<string, unknown>, filename?: string): Promise<void> {
  const { blobUrl, name } = await fetchPdf(url, params, filename);
  triggerDownload(blobUrl, name);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

function triggerDownload(blobUrl: string, name: string): void {
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Ouvre OU télécharge selon `mode` (« open » par défaut). */
export async function renderPdf(url: string, params?: Record<string, unknown>, mode: PdfMode = 'open', filename?: string): Promise<void> {
  return mode === 'download' ? downloadPdf(url, params, filename) : openPdf(url, params, filename);
}
